import { ArrowRight, TrendingUp, Globe2, FileText, Building2, Users, Target, Lightbulb, Shield, BookOpen, Heart, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  const intelligencePillars = [
    {
      icon: <TrendingUp className="w-10 h-10 text-[#007BFF]" />,
      title: 'Commodity Research',
      description: 'In-depth research across global energy commodities, covering supply-demand dynamics, pricing drivers, trade flows, infrastructure, regulatory shifts, and investment trends.'
    },
    {
      icon: <Globe2 className="w-10 h-10 text-[#007BFF]" />,
      title: 'Country Analysis',
      description: 'Comprehensive country-level energy intelligence including policy frameworks, fiscal regimes, licensing activity, infrastructure readiness, and geopolitical risk.'
    },
    {
      icon: <FileText className="w-10 h-10 text-[#007BFF]" />,
      title: 'Market Intelligence',
      description: 'Structured monitoring of market movements, project developments, competitive landscapes, capital allocation trends, and emerging strategic themes.'
    },
    {
      icon: <Building2 className="w-10 h-10 text-[#007BFF]" />,
      title: 'Specialized Reports',
      description: 'Board-ready analytical reports supporting strategic planning, valuation analysis, capital deployment decisions, and stakeholder communication.'
    }
  ];

  const coverage = [
    'Commodity Prices', 'Crude Oil', 'Natural Gas', 'Coal',
    'Solar Energy', 'Wind Energy', 'Bio Energy', 'Geothermal Energy',
    'Hydropower', 'Nuclear Energy', 'Critical Minerals', 'Emissions Tracking'
  ];

  const approach = [
    {
      icon: <Target className="w-8 h-8 text-[#007BFF]" />,
      title: 'Structure',
      description: 'Organizing complex energy information into accessible, decision-useful intelligence.'
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-[#007BFF]" />,
      title: 'Clarity',
      description: 'Translating technical and regulatory complexity into strategic insight.'
    },
    {
      icon: <Shield className="w-8 h-8 text-[#007BFF]" />,
      title: 'Relevance',
      description: 'Focusing on intelligence that materially impacts capital, operations, and policy outcomes.'
    }
  ];

  const customers = [
    { icon: <Building2 className="w-6 h-6" />, text: 'Energy Companies Across The Value Chain' },
    { icon: <TrendingUp className="w-6 h-6" />, text: 'Investors And Financial Institutions' },
    { icon: <Target className="w-6 h-6" />, text: 'Corporate Strategy And Planning Teams' },
    { icon: <Users className="w-6 h-6" />, text: 'Advisory Firms And Consultants' },
    { icon: <Globe2 className="w-6 h-6" />, text: 'Government Agencies And Policymakers' },
    { icon: <FileText className="w-6 h-6" />, text: 'Research Institutions And Analysts' }
  ];

  const subLinks = [
    {
      icon: <BookOpen className="w-8 h-8 text-[#007BFF]" />,
      title: 'Our Story',
      teaser: 'The evolution from concept to global intelligence platform',
      link: '/about/story'
    },
    {
      icon: <Users className="w-8 h-8 text-[#007BFF]" />,
      title: 'Our People',
      teaser: 'Expert-driven intelligence and multidisciplinary expertise',
      link: '/about/people'
    },
    {
      icon: <Shield className="w-8 h-8 text-[#007BFF]" />,
      title: 'Our Values',
      teaser: 'Principles guiding our intelligence delivery and client commitment',
      link: '/about/values'
    },
    {
      icon: <Heart className="w-8 h-8 text-[#007BFF]" />,
      title: 'Our Culture',
      teaser: 'Intellectual rigor, collaborative excellence, and analytical discipline',
      link: '/about/culture'
    },
    {
      icon: <Building2 className="w-8 h-8 text-[#007BFF]" />,
      title: 'Our Customers',
      teaser: 'Trusted partner across the global energy ecosystem',
      link: '/about/customers'
    },
    {
      icon: <MapPin className="w-8 h-8 text-[#007BFF]" />,
      title: 'Our Locations',
      teaser: 'Global coverage with digital-first intelligence delivery',
      link: '/about/locations'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">About Enerlligence Analytics</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              An energy intelligence platform delivering credible insights and analytical clarity across global energy markets.
            </p>
          </div>
        </div>
      </section>

      {/* Learn More Sub-Links - Immediately After Hero */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Learn More About Enerlligence</h2>
            <p className="text-lg text-slate-600">Explore our story, people, values, and approach</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subLinks.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className="group bg-[#F5F5F5] p-8 rounded-lg border border-slate-200 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-[#1F3C88] mb-3 group-hover:text-[#007BFF] transition-colors">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-3">
                  {item.teaser}
                </p>
                <div className="flex items-center text-[#007BFF] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more
                  <ArrowRight className="ml-2 w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Our Mission</h2>
          <p className="text-xl text-slate-700 leading-relaxed">
            To provide trusted, structured, and forward-looking energy intelligence that empowers better strategic, investment, and policy decisions.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">What We Do</h2>
            <p className="text-lg text-slate-600">
              Structured intelligence pillars designed for strategic decision-making
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {intelligencePillars.map((pillar, index) => (
              <div
                key={index}
                className="bg-slate-50 p-8 rounded-lg border-2 border-slate-200 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 group"
              >
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {pillar.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#1F3C88] mb-4">
                  {pillar.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">What We Cover</h2>
            <p className="text-lg text-slate-600">
              Comprehensive coverage across the global energy landscape
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {coverage.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg border border-slate-200 hover:border-[#007BFF] hover:shadow-md transition-all duration-200 cursor-pointer group text-center"
              >
                <p className="text-[#1F3C88] font-medium text-sm group-hover:text-[#007BFF] transition-colors">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Our Approach</h2>
            <p className="text-lg text-slate-600">
              Strategic philosophy guiding our intelligence delivery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {approach.map((item, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-50 rounded-full mb-6 border-2 border-[#007BFF]">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-[#1F3C88] mb-4">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Who We Serve</h2>
            <p className="text-lg text-slate-600">
              Enerlligence supports decision-makers across the energy ecosystem
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {customers.map((customer, index) => (
              <div
                key={index}
                className="flex items-center space-x-4 p-6 bg-white rounded-lg border border-slate-200 hover:border-[#007BFF] hover:shadow-md transition-all duration-200"
              >
                <div className="text-[#007BFF] flex-shrink-0">
                  {customer.icon}
                </div>
                <p className="text-[#1F3C88] font-medium text-sm">{customer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - Gradient Background */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Make Better Energy Decisions with Confidence
          </h2>
          <p className="text-lg text-blue-100 mb-10">
            Partner with Enerlligence for trusted energy intelligence
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#1D3B87] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
            >
              Request a Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-md border-2 border-white/30 hover:bg-white/10 transition-all duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
